import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import Header from "./components/Header";
import AuthenticatedLayout from "./components/AuthenticatedLayout";
import CookieConsent from "./components/CookieConsent";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import Leaderboard from "./pages/Leaderboard";
import Assessment from "./pages/Assessment";
import Assessments from "./pages/Assessments";
import AssessmentReport from "./pages/AssessmentReport";
import AssessmentAttempts from "./pages/AssessmentAttempts";
import LearningPaths from "./pages/LearningPaths";
import Progress from "./pages/Progress";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import ActivityMonitor from "./pages/admin/ActivityMonitor";
import TopAssessments from "./pages/admin/TopAssessments";
import UserManagement from "./pages/admin/UserManagement";
import AssessmentManagement from "./pages/admin/AssessmentManagement";
import AssessmentDetails from "./pages/admin/AssessmentDetails";
import CreateAssessment from "./pages/CreateAssessment";
import NotFound from "./pages/NotFound";
import Footer from "./components/Footer";
import "./App.css";

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Public routes with header and footer */}
              <Route
                path="/"
                element={
                  <>
                    <Header />
                    <Home />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/about"
                element={
                  <>
                    <Header />
                    <About />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/contact"
                element={
                  <>
                    <Header />
                    <Contact />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/privacy"
                element={
                  <>
                    <Header />
                    <Privacy />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/terms"
                element={
                  <>
                    <Header />
                    <Terms />
                    <Footer />
                  </>
                }
              />
              <Route
                path="/login"
                element={
                  <>
                    <Header />
                    <Login />
                    <Footer />
                  </>
                }
              />

              {/* Authenticated routes with sidebar */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <Dashboard />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <Settings />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* User-only routes */}
              <Route
                path="/assessments"
                element={
                  <ProtectedRoute requiredPermission="take_assessments">
                    <AuthenticatedLayout>
                      <Assessments />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessment/:assessmentId"
                element={
                  <ProtectedRoute requiredPermission="take_assessments">
                    <AuthenticatedLayout>
                      <Assessment />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessment/:assessmentId/attempts"
                element={
                  <ProtectedRoute requiredPermission="take_assessments">
                    <AuthenticatedLayout>
                      <AssessmentAttempts />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/learning-paths"
                element={
                  <ProtectedRoute requiredPermission="view_learning_paths">
                    <AuthenticatedLayout>
                      <LearningPaths />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/progress"
                element={
                  <ProtectedRoute requiredPermission="view_progress">
                    <AuthenticatedLayout>
                      <Progress />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/leaderboard"
                element={
                  <ProtectedRoute>
                    <AuthenticatedLayout>
                      <Leaderboard />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessment-report/:reportId"
                element={
                  <ProtectedRoute requiredPermission="view_progress">
                    <AuthenticatedLayout>
                      <AssessmentReport />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* Admin-only routes */}
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <AdminDashboard />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <AdminAnalytics />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/activity"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <ActivityMonitor />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/top-assessments"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <TopAssessments />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <UserManagement />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessments"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <AssessmentManagement />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/create-assessment"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <CreateAssessment />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/assessment-details/:assessmentId"
                element={
                  <ProtectedRoute adminOnly>
                    <AuthenticatedLayout>
                      <AssessmentDetails />
                    </AuthenticatedLayout>
                  </ProtectedRoute>
                }
              />

              {/* 404 route */}
              <Route
                path="*"
                element={
                  <>
                    <Header />
                    <NotFound />
                    <Footer />
                  </>
                }
              />
            </Routes>

            {/* Cookie Consent Banner */}
            <CookieConsent />
          </div>
        </Router>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
